import type { ButtonPriority } from '@timelyapp/react';
import Search from 'components/Search';
import UserSearchOption from 'components/users/UserSearchOption';
import { omit } from 'lodash-es';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { fetchProjectIdSmartly } from 'redux/modules/projects.actions';
import type { UserDropdownValue } from 'redux/selectors/users';
import { makeGetUserSearchOptions } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import type { ThunkDispatch } from 'types/redux';

const matchOption = (option: UserDropdownValue, filterString: string): boolean => {
  if (!filterString) {
    return true;
  }
  const search = filterString.toLowerCase();

  const name = (option.label || '').toLowerCase();
  const email = (option.email || '').toLowerCase();

  if (name.includes(search) || email.includes(search)) {
    return true;
  }
  return false;
};

interface ExternalProps {
  value?: number | null; // defaultProp'd to null,
  onChange: (userId: string | number | null) => void;
  shrinkOnBlur?: boolean;
  shrinkLabel?: React.ReactNode;
  buttonPriority?: ButtonPriority;
  placeholder?: string;
  popoverFixed?: boolean;
  variant?: 'default' | 'inHeader';
  className?: string;
  name?: string;
  forProjectId?: number;
  /** Replace the search icon with something else? */
  inputIcon?: React.ReactNode;
  includeEmpty?: string;
  includeDeleted?: boolean;
}

interface StateProps {
  membershipLoaded: boolean;
  users: Array<UserDropdownValue>;
}

const makeMapStateToProps = () => {
  const getUserSearchOptions = makeGetUserSearchOptions();

  const mapStateToProps = (state: IState, ownProps: ExternalProps) => {
    const project = ownProps.forProjectId
      ? state.projects.projectById[ownProps.forProjectId]
      : undefined;
    return {
      membershipLoaded: project?.type === 'full' && project.project.users != null,
      users: getUserSearchOptions(state, {
        forProjectId: ownProps.forProjectId,
        initialUserId: ownProps.value,
        includeDeleted: !!ownProps.includeDeleted,
      }),
    };
  };
  return mapStateToProps;
};

type Props = ExternalProps & StateProps;

const UserSearch: React.FC<Props> = ({
  users,
  membershipLoaded,
  value,
  popoverFixed,
  onChange,
  forProjectId,
  includeEmpty,
  includeDeleted,
  ...rest
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  useEffect(() => {
    if (forProjectId && !membershipLoaded) {
      dispatch(fetchProjectIdSmartly(forProjectId, { needUsers: true }));
    }
  }, [dispatch, forProjectId, membershipLoaded]);

  const handleChange = useCallback(
    (userId: string | number | null) => {
      onChange(userId === -1 ? null : userId);
    },
    [onChange]
  );

  const options: typeof users = useMemo(() => {
    if (includeEmpty) {
      return [
        {
          value: -1,
          label: includeEmpty,
          empty: true,
          avatar: null,
          email: null,
        } as UserDropdownValue,
      ].concat(users);
    }
    return users;
  }, [users, includeEmpty]);

  return (
    <Search<UserDropdownValue>
      hideFromFullstory
      data-user-search
      popoverFixed={popoverFixed}
      data-testid="manage_teams_usersearchjs"
      optionComponent={UserSearchOption}
      matchOption={matchOption}
      options={options}
      optionHeight={30}
      value={value}
      onChange={handleChange}
      {...omit(rest, 'dispatch', 'forProjectId')}
    />
  );
};

UserSearch.defaultProps = {
  value: null,
  variant: 'default' as const,
  placeholder: 'Name or email',
};

export default connect<StateProps, {}, ExternalProps, IState>(makeMapStateToProps)(UserSearch);
